/**
 * Die Markierungsebene über einer **Videoaufnahme** — dieselben Marken wie über einem Foto,
 * nur zeitlich begrenzt.
 *
 * **Ein Block ist eine Gruppe von Marken mit Anfang und Ende.** Steht die Wiedergabe in seinem
 * Bereich, sind seine Marken zu sehen; verlässt sie ihn, sind sie weg. Mehrere Blöcke dürfen
 * sich überschneiden — dann gelten sie gleichzeitig und liegen in der Leiste untereinander
 * (`track`).
 *
 * **Die Marken kommen unverändert aus `domain/annotation.ts`.** Sie stehen in Videopixeln,
 * Ursprung oben links, und die `viewBox` der Zeichenebene trägt die Videogrösse — dieselbe
 * Mechanik wie beim Foto, damit Drehen und Skalieren ohne Rechnung auskommen.
 */

import type { Marker, MarkerKind, SizeStep } from './annotation';

export interface VideoOverlay {
  /** Fassung des Dateiformats. Steigt, sobald ein Feld seine Bedeutung ändert. */
  version: number;
  /** Breite des Videobildes in Pixeln — zugleich die Breite der `viewBox`. */
  width: number;
  height: number;
  /** Gesamtlänge der Aufnahme in ganzen Millisekunden. */
  durationMs: number;
  blocks: Block[];
}

export interface Block {
  id: string;
  /** Anfang in **ganzen Millisekunden** seit Videobeginn. */
  fromMs: number;
  /** Ende in ganzen Millisekunden, ausschliesslich (s. `bloeckeBei`). */
  toMs: number;
  /** Zeile in der Blockleiste, von oben ab 0. Überschneidende Blöcke stehen untereinander. */
  track: number;
  /** In Stapelfolge: die letzte Marke liegt oben. */
  markers: Marker[];
}

/**
 * Welche Blöcke gelten am Zeitpunkt `ms`.
 *
 * ⚠️ **Halboffen (`fromMs <= ms < toMs`).** Stossen zwei Blöcke aneinander, gälten sie am
 * Berührungspunkt sonst beide — für einen Augenblick stünden die Marken beider übereinander.
 */
export function bloeckeBei(blocks: Block[], ms: number): Block[] {
  return blocks.filter((b) => b.fromMs <= ms && ms < b.toMs);
}

/**
 * Die oberste Spur, auf der sich der neue Bereich mit keinem vorhandenen Block überschneidet.
 *
 * ⚠️ **Überschneidung ist erlaubt, nicht verboten.** Zwei gleichzeitig gültige Blöcke sind ein
 * gewollter Fall — der Prüfer darf einen Kreis über die ganze Szene legen und darin einzelne
 * Stellen mit Pfeilen versehen. Die Spur trennt sie nur in der Leiste, damit beide mit dem
 * Finger zu fassen bleiben.
 *
 * ⚠️ **Ein Block, der genau dort endet, wo der neue beginnt, ist kein Hindernis** — dieselbe
 * halboffene Rechnung wie in `bloeckeBei`, sonst rutschte jede lückenlose Folge Spur um Spur
 * nach unten.
 *
 * Gesucht wird die **oberste** freie Spur, nicht die nächste unter der letzten: wird ein Block
 * gelöscht, soll die frei gewordene Zeile wieder benutzt werden.
 */
export function spurFuer(blocks: Block[], fromMs: number, toMs: number): number {
  const belegt = (spur: number) =>
    blocks.some((b) => b.track === spur && b.fromMs < toMs && fromMs < b.toMs);

  for (let spur = 0; ; spur++) {
    if (!belegt(spur)) return spur;
  }
}


/** Die Fassung, die diese App schreibt. */
export const FASSUNG = 1;

/**
 * Die Ebene als Text, wie er in `<video>-annotations.json` steht.
 *
 * ⚠️ **Eingerückt, nicht gepresst.** Die Datei ist klein, und wer einem Fehler nachgeht, liest
 * sie von Hand — beim Foto ist das SVG aus demselben Grund zeilenweise aufgebaut.
 */
export function zuJson(overlay: VideoOverlay): string {
  return JSON.stringify(overlay, null, 2) + '\n';
}

export class VideoAnnotationLeseFehler extends Error {
  constructor(grund: string) {
    super(grund);
    this.name = 'VideoAnnotationLeseFehler';
  }
}

const STUFEN: SizeStep[] = ['klein', 'mittel', 'gross'];

/** Welche Grössenstufe eine Marke führt — die Formen `stroke`, die Schriften `size`. */
const STUFENFELD: Partial<Record<MarkerKind, 'stroke' | 'size'>> = {
  arrow: 'stroke',
  ellipse: 'stroke',
  rect: 'stroke',
  freehand: 'stroke',
  text: 'size',
  number: 'size',
};

/** Die Zahlenfelder je Art — `cover` und `rect` teilen sich dieselben. */
const ZAHLEN: Record<MarkerKind, string[]> = {
  arrow: [],
  ellipse: ['cx', 'cy', 'rx', 'ry'],
  rect: ['x', 'y', 'w', 'h'],
  freehand: [],
  text: ['x', 'y'],
  number: ['x', 'y', 'n'],
  cover: ['x', 'y', 'w', 'h'],
};

const istObjekt = (w: unknown): w is Record<string, unknown> =>
  typeof w === 'object' && w !== null && !Array.isArray(w);

const zahl = (w: unknown): w is number => typeof w === 'number' && Number.isFinite(w);

const ganz = (w: unknown): w is number => zahl(w) && Number.isInteger(w);

function pruefePunkt(w: unknown, wo: string): void {
  if (!istObjekt(w) || !zahl(w.x) || !zahl(w.y)) {
    throw new VideoAnnotationLeseFehler(wo + ' ist kein Punkt mit x und y.');
  }
}

function pruefeMarke(w: unknown, wo: string): void {
  if (!istObjekt(w)) throw new VideoAnnotationLeseFehler(wo + ' ist keine Marke.');

  if (typeof w.id !== 'string' || w.id === '') {
    throw new VideoAnnotationLeseFehler(wo + ' hat keine Kennung.');
  }
  if (typeof w.color !== 'string' || w.color === '') {
    throw new VideoAnnotationLeseFehler(wo + ' hat keine Farbe.');
  }

  const art = w.kind;
  if (typeof art !== 'string' || !(art in ZAHLEN)) {
    throw new VideoAnnotationLeseFehler(wo + ' führt die unbekannte Art ' + String(art) + '.');
  }
  const kind = art as MarkerKind;

  const feld = STUFENFELD[kind];
  if (feld) {
    const stufe = w[feld];
    if (!STUFEN.some((s) => s === stufe)) {
      throw new VideoAnnotationLeseFehler(
        wo + ' führt die unbekannte Grössenstufe ' + String(stufe) + '.',
      );
    }
  }

  for (const name of ZAHLEN[kind]) {
    if (!zahl(w[name])) {
      throw new VideoAnnotationLeseFehler(wo + ': das Feld ' + name + ' ist keine Zahl.');
    }
  }

  if (kind === 'arrow') {
    pruefePunkt(w.von, wo + ': von');
    pruefePunkt(w.bis, wo + ': bis');
  }
  if (kind === 'freehand') {
    if (!Array.isArray(w.punkte)) {
      throw new VideoAnnotationLeseFehler(wo + ' hat keine Punktliste.');
    }
    w.punkte.forEach((p, i) => pruefePunkt(p, wo + ': Punkt ' + i));
  }
  if (kind === 'text' && typeof w.text !== 'string') {
    throw new VideoAnnotationLeseFehler(wo + ' hat keinen Text.');
  }
}

function pruefeBlock(w: unknown, nr: number): void {
  if (!istObjekt(w)) throw new VideoAnnotationLeseFehler('Block ' + nr + ' ist kein Objekt.');

  const id = typeof w.id === 'string' && w.id !== '' ? w.id : null;
  if (!id) throw new VideoAnnotationLeseFehler('Block ' + nr + ' hat keine Kennung.');

  /** Ab hier trägt jede Meldung die Kennung: bei zwanzig Blöcken sagt „Block 13" zu wenig. */
  const wo = 'Block ' + id;

  if (!ganz(w.fromMs) || !ganz(w.toMs)) {
    throw new VideoAnnotationLeseFehler(wo + ' führt keine ganzen Millisekunden.');
  }
  if (w.fromMs < 0) {
    throw new VideoAnnotationLeseFehler(wo + ' beginnt vor dem Videoanfang.');
  }
  if (w.toMs <= w.fromMs) {
    throw new VideoAnnotationLeseFehler(wo + ' endet, bevor er anfängt.');
  }
  if (!ganz(w.track) || w.track < 0) {
    throw new VideoAnnotationLeseFehler(wo + ' liegt auf keiner gültigen Spur.');
  }
  if (!Array.isArray(w.markers)) {
    throw new VideoAnnotationLeseFehler(wo + ' hat keine Markenliste.');
  }
  w.markers.forEach((m, i) => pruefeMarke(m, wo + ', Marke ' + i));
}

/**
 * Liest eine Ebene aus ihrer Datei — **mit Prüfung**.
 *
 * ⚠️ **Eine Datei, die hier durchkommt, hat der Server nachher zu zeichnen.** Was unlesbar ist,
 * soll deshalb an dieser Stelle auffallen und nicht erst dort: eine Marke ohne Farbe oder ein
 * Block ohne Länge käme sonst als stumme Lücke in der Auswertung an.
 *
 * ⚠️ **Der Fehler nennt den Block bei seiner Kennung.** „Block 13" hilft niemandem, der eine
 * Datei mit zwanzig Blöcken vor sich hat.
 */
export function ausJson(text: string): VideoOverlay {
  let roh: unknown;
  try {
    roh = JSON.parse(text);
  } catch {
    throw new VideoAnnotationLeseFehler('Die Datei ist kein JSON.');
  }

  if (!istObjekt(roh)) throw new VideoAnnotationLeseFehler('Die Datei trägt kein Objekt.');

  if (roh.version !== FASSUNG) {
    throw new VideoAnnotationLeseFehler(
      'Unbekannte Fassung ' + String(roh.version) + ', erwartet ' + FASSUNG + '.',
    );
  }
  if (!zahl(roh.width) || !zahl(roh.height) || roh.width <= 0 || roh.height <= 0) {
    throw new VideoAnnotationLeseFehler('Die Videogrösse fehlt oder ist unbrauchbar.');
  }
  if (!ganz(roh.durationMs) || roh.durationMs <= 0) {
    throw new VideoAnnotationLeseFehler('Die Videolänge fehlt oder ist unbrauchbar.');
  }
  if (!Array.isArray(roh.blocks)) {
    throw new VideoAnnotationLeseFehler('Die Datei hat keine Blockliste.');
  }

  roh.blocks.forEach(pruefeBlock);

  return roh as unknown as VideoOverlay;
}

/**
 * Eine Ebene lesen, ohne dass ein Fehler die Liste mitreisst — für die Videokommentare, die
 * auf einen Blick zeigen sollen, ob und wie eine Aufnahme markiert ist.
 *
 * ⚠️ **Wirft nie.** Kein Text (es gibt keine Datei), kaputtes JSON, eine fremde Fassung: alles
 * ergibt `null`. Eine Liste, die wegen einer einzigen unlesbaren Datei gar nichts mehr
 * anzeigt, wäre schlimmer als eine fehlende Markierung — die Bearbeitung selbst meldet den
 * Fehler ohnehin.
 *
 * ⬅️ **Lieferte bis zum 2026-09-20 nur die Blockzahl** (`blockZahlAus`). Damit war die Datei
 * gelesen und sofort weggeworfen, und die Vorschaukachel hatte nichts, was sie hätte zeigen
 * können — der Grund, warum dort nie eine Annotation zu sehen war.
 */
export function ebeneAus(text: string | null): VideoOverlay | null {
  if (!text) return null;
  try {
    return ausJson(text);
  } catch {
    return null;
  }
}
